import React from 'react';
import {
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';

import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import KeyIcon from '@mui/icons-material/Key';
import VisibilityIcon from '@mui/icons-material/Visibility';

import { APIClientRecord } from '../../../hooks/api-client-hooks';

import {
  clientOwnerLabel,
  dateLabel,
} from './apiClientUtils';

type Props = {
  rows: APIClientRecord[];
  loading?: boolean;
  canUpdate: boolean;
  canDelete: boolean;
  roleNameById: Map<number, string>;
  rotateLoading?: boolean;
  deleteLoading?: boolean;
  onEdit: (client: APIClientRecord) => void;
  onView: (client: APIClientRecord) => void;
  onRotateSecret: (client: APIClientRecord) => void;
  onDelete: (client: APIClientRecord) => void;
};

const APIClientsTable: React.FC<Props> = ({
                                            rows,
                                            loading,
                                            canUpdate,
                                            canDelete,
                                            roleNameById,
                                            rotateLoading,
                                            deleteLoading,
                                            onEdit,
                                            onView,
                                            onRotateSecret,
                                            onDelete,
                                          }) => {
  return (
    <TableContainer sx={{ maxHeight: 620 }}>
      <Table stickyHeader size="small">
        <TableHead>
          <TableRow>
            <TableCell>Name</TableCell>
            <TableCell>Owner</TableCell>
            <TableCell width={110}>Enabled</TableCell>
            <TableCell width={130}>TTL</TableCell>
            <TableCell>Roles</TableCell>
            <TableCell width={190}>Updated</TableCell>
            <TableCell width={170} align="right">Actions</TableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {rows.map((client) => (
            <TableRow key={client.client_id} hover>
              <TableCell>
                <Stack spacing={0.25}>
                  <Typography variant="body2" fontWeight={700}>
                    {client.name}
                  </Typography>

                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ wordBreak: 'break-all' }}
                  >
                    {client.client_id}
                  </Typography>

                  {client.description ? (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ wordBreak: 'break-word' }}
                    >
                      {client.description}
                    </Typography>
                  ) : null}
                </Stack>
              </TableCell>

              <TableCell>
                <Typography variant="body2">
                  {clientOwnerLabel(client)}
                </Typography>

                {client.owner_id ? (
                  <Typography variant="caption" color="text.secondary">
                    ID: {client.owner_id}
                  </Typography>
                ) : null}
              </TableCell>

              <TableCell>
                <Chip
                  size="small"
                  color={client.enabled ? 'success' : 'default'}
                  variant={client.enabled ? 'filled' : 'outlined'}
                  label={client.enabled ? 'yes' : 'no'}
                />
              </TableCell>

              <TableCell>
                <Typography variant="body2">
                  {client.token_ttl_seconds || 3600}s
                </Typography>
              </TableCell>

              <TableCell>
                <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                  {(client.role_ids || []).map((roleId) => (
                    <Chip
                      key={roleId}
                      size="small"
                      variant="outlined"
                      label={roleNameById.get(Number(roleId)) || `Role ${roleId}`}
                    />
                  ))}

                  {!(client.role_ids || []).length ? (
                    <Typography variant="caption" color="text.secondary">
                      No roles
                    </Typography>
                  ) : null}
                </Stack>
              </TableCell>

              <TableCell>
                <Typography variant="caption">
                  {dateLabel(client.last_updated)}
                </Typography>

                {client.updated_by ? (
                  <Typography variant="caption" color="text.secondary" display="block">
                    by {client.updated_by}
                  </Typography>
                ) : null}
              </TableCell>

              <TableCell align="right">
                <Tooltip title={canUpdate ? 'Edit' : 'View'}>
                  <IconButton
                    size="small"
                    onClick={() => (canUpdate ? onEdit(client) : onView(client))}
                  >
                    {canUpdate ? <EditIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                  </IconButton>
                </Tooltip>

                <Tooltip title="Rotate Secret">
                  <span>
                    <IconButton
                      size="small"
                      color="warning"
                      disabled={!canUpdate || !!rotateLoading}
                      onClick={() => onRotateSecret(client)}
                    >
                      <KeyIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>

                <Tooltip title="Delete">
                  <span>
                    <IconButton
                      size="small"
                      color="error"
                      disabled={!canDelete || !!deleteLoading}
                      onClick={() => onDelete(client)}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </TableCell>
            </TableRow>
          ))}

          {!rows.length && !loading ? (
            <TableRow>
              <TableCell colSpan={7}>
                <Typography variant="body2" color="text.secondary">
                  No API clients found.
                </Typography>
              </TableCell>
            </TableRow>
          ) : null}

          {loading ? (
            <TableRow>
              <TableCell colSpan={7}>
                <Stack direction="row" spacing={1} alignItems="center" py={2}>
                  <CircularProgress size={18} />
                  <Typography variant="body2" color="text.secondary">
                    Loading API clients...
                  </Typography>
                </Stack>
              </TableCell>
            </TableRow>
          ) : null}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default APIClientsTable;